import type { ChatMessage, ReceiveData } from '@/api/ai/aiType';
import type { AiConversationSource } from '@/api/ai/sources';
import { connect } from '@/utils/sse';
import { error } from '@/utils/toast';
import {
  del,
  get,
  getStoredAccessToken,
  post,
  postRaw,
  put,
  refreshAccessToken,
} from '../request';

interface ChatRequest extends Record<string, unknown> {
  messages: ChatMessage[];
  conversationId?: string;
  attachments?: number[]; // 附件 ID 数组
  referencedAttachmentIds?: number[];
  source?: AiConversationSource;
  responseFormat?: 'text' | 'json_object';
}

export interface AiFinishResult {
  hasValidText: boolean;
  isUserAbort: boolean;
  retriesExhausted: boolean;
  reason: 'closed' | 'error';
}

function buildChatStreamHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': '*/*',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function getErrorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object' || !('status' in err)) {
    return undefined;
  }

  const status = Number((err as { status?: unknown }).status);
  return Number.isFinite(status) ? status : undefined;
}

async function aiApi(
  requestData: ChatRequest,
  open: () => void,
  receive: (data: ReceiveData) => void,
  finish: (result?: AiFinishResult) => void,
  abortController: AbortController | null = null,
  options?: { maxReconnects?: number },
) {
  const controller = abortController || new AbortController();
  let hasValidText = false;
  let finished = false;

  const finalize = (result: AiFinishResult) => {
    if (finished) {
      return;
    }
    finished = true;
    finish(result);
  };

  const connectWithToken = async (token: string | null) => {
    await connect({
      url: '/langchain/chat/',
      method: 'POST',
      body: requestData,
      headers: buildChatStreamHeaders(token),
      controller,
      firstTokenTimeoutMs: 45000,
      maxReconnects: options?.maxReconnects ?? 1,
      isUserAbort: () => controller.signal.aborted,
      onopen: async (_response) => {
        console.log('SSE connection established');
        open();
      },
      onmessage: (ev) => {
        if (!ev.data) {
          return;
        }

        try {
          const parsed = JSON.parse(ev.data) as { type?: string; content?: unknown };
          if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
            return;
          }

          if (parsed.type === 'ping') {
            return;
          }

          if (parsed.type === 'text' && typeof parsed.content === 'string' && parsed.content.trim()) {
            hasValidText = true;
          }

          receive(parsed as ReceiveData);
        }
        catch {
          // Ignore non-JSON chunks and keepalive packets.
        }
      },
      onclose: (meta) => {
        console.log('SSE connection closed', meta);
        finalize({
          hasValidText,
          isUserAbort: controller.signal.aborted,
          retriesExhausted: true,
          reason: 'closed',
        });
      },
      onerror: (ev, meta) => {
        console.error('SSE error:', ev, meta);
      },
    });
  };

  try {
    try {
      await connectWithToken(getStoredAccessToken());
    }
    catch (streamError) {
      if (controller.signal.aborted || getErrorStatus(streamError) !== 401) {
        throw streamError;
      }

      const refreshedToken = await refreshAccessToken();
      if (controller.signal.aborted) {
        return;
      }
      await connectWithToken(refreshedToken);
    }
  }
  catch (e) {
    const isUserAbort = controller.signal.aborted;
    if (!isUserAbort) {
      error('连接发生错误');
    }
    console.error('Error during SSE connection:', e);
    finalize({
      hasValidText,
      isUserAbort,
      retriesExhausted: true,
      reason: 'error',
    });
  }
}
async function generateTitleApi(messages: ChatMessage[]) {
  return await postRaw<{
    title: string;
  }>('/ai/title', { messages });
}

export async function updateConversationTitleApi(conversationId: string, title: string) {
  const result = await put<{
    conversation: {
      id: string;
      title: string;
      source: AiConversationSource;
    };
  }>(`/langchain/conversation/${conversationId}/`, { title });
  return result.data;
}

export async function deleteConversationApi(conversationId: string) {
  const result = await del<null>(`/langchain/conversation/${conversationId}/`);
  return result.data;
}

async function generateDistributionFunctionApi(distribution: string) {
  return await postRaw<{
    function: string;
    latex: string;
    min: number;
    max: number;
  }>('/ai/distribution_function', { distribution }, {
    timeout: 20000,
  });
}

async function fetchQuestionsApi() {
  const result = await get<{
    questions: string[];
  }>(`/langchain/questions/`);
  return result.data;
}

async function fetchWordCloudApi() {
  const result = await post<{
    wordsData: {
      words: string[];
      total_words: number;
      frequencies: number[];
    }[];
    wordCount: number;
    imageUrl: string;
    id: string;
  }>(`/langchain/wordcloud/`);
  return result.data;
}

// 获取单个会话的详细消息
export async function getConversationApi(conversationId: string) {
  const result = await get<{
    messages: Array<{
      id: string;
      messageType: 'user' | 'assistant' | 'tool' | 'system';
      content: string;
      createdAt: string;
      toolCalls?: any;
      toolCallId?: string;
      toolName?: string;
      isDeleted?: boolean; // 新增：软删除标记
      editedAt?: string; // 新增：编辑时间
      editCount?: number; // 新增：编辑次数
      attachments?: {
        attachmentType: string;
        createdAt: string;
        fileSize: number;
        fileUrl: string;
        filename: string;
        id: number;
        mimeType: string;
        parseStatus: string;
        summaryPreview: string;
      }[];
    }>;
  }>(`/langchain/conversation/${conversationId}/`);
  return result.data;
}

// 获取用户所有会话列表
export async function getConversationsListApi(source?: string) {
  const params = source ? `?source=${encodeURIComponent(source)}` : '';
  const result = await get<{
    conversations: Array<{
      id: string;
      title: string;
      source: AiConversationSource;
      updated_at: string;
      message_count: number;
      user: { id: number; username: string };
      subject: { id: number; name: string } | null;
    }>;
  }>(`/langchain/conversations/${params}`);
  return result.data;
}

// 文件上传相关类型
export interface UploadedAttachment {
  id: number;
  attachmentType: 'image' | 'file';
  filename: string;
  fileSize: number;
  mimeType: string;
  parseStatus: string;
  summaryPreview: string;
  fileUrl: string;
  createdAt: string;
}

// 文件上传接口
async function uploadFileApi(file: File, purpose?: 'subjective_answer'): Promise<UploadedAttachment> {
  console.log('[uploadFileApi] 开始上传文件:', file.name, file.size, 'bytes');
  const formData = new FormData();
  formData.append('file', file);
  if (purpose)
    formData.append('purpose', purpose);
  try {
    const result = await post<UploadedAttachment>('/langchain/upload/', formData, {
      timeout: 60000, // 60 秒超时
    });
    console.log('[uploadFileApi] 上传成功:', result.data);
    return result.data;
  }
  catch (error) {
    console.error('[uploadFileApi] 上传失败:', error);
    throw error;
  }
}

export {
  aiApi,
  fetchQuestionsApi,
  fetchWordCloudApi,
  generateDistributionFunctionApi,
  generateTitleApi,
  uploadFileApi,
};
